<script setup lang="ts">
import type { FlightHydrated } from "@ca/db/flights";
import type { FlightProcessingBucket } from "@ca/types/flights";

import { computed } from "vue";

import { displayAirportCode } from "@ca/formatters/airports";
import { formatDayMonth } from "@ca/formatters/dates";
import { formatDistance } from "@ca/formatters/distance";

import { useFlightDuration } from "@/composables/flights";
import { isFlightProcessing } from "@/utils/flightProgress";

import FlightEventsIcon from "./FlightEventsIcon.vue";
import FlightProcessingBadge from "./FlightProcessingBadge.vue";
import RulerIcon from "./icons/RulerIcon.vue";
import RiskBadge from "./RiskBadge.vue";
const props = defineProps<{
	bucket?: FlightProcessingBucket;
	flight: FlightHydrated;
	landingThreshold: number;
	shortStripLandingsIds: Array<string>;
	lateNightFlightsIds: Array<string>;
}>();

const processing = computed(() =>
	!props.flight.risk?.riskLevel && isFlightProcessing(props.bucket) ? props.bucket : null);

const dateLabel = computed(() => {
	return formatDayMonth(props.flight.timeIndex);
});

const airports = computed(() => {
	return {
		origin: displayAirportCode(props.flight.originAirport),
		destination: displayAirportCode(props.flight.destinationAirport),
	};
});

const duration = useFlightDuration(props.flight);
const isShortStripLanding = computed(() => {
	if (props.flight.destinationRunway?.length) {
		return props.flight.destinationRunway?.length < props.landingThreshold;
	} else if (props.shortStripLandingsIds.includes(props.flight.id)) {
		return true;
	}
    
	return false;
});

const shortStripLandingTooltip = computed(() => {
	if (props.flight.destinationRunway?.length) {
		return `Short Strip Landing: ${formatDistance({ distance: props.flight.destinationRunway?.length, unit: "foot" })}`;
	}
	return "Short Strip Landing: < " + formatDistance({ distance: props.landingThreshold, unit: "foot" });
});

const isLateNightFlight = computed(() => {
	return props.lateNightFlightsIds.includes(props.flight.id);
});

</script>
  
<template>
  <UCard variant="subtle">
    <template #header>
      <div class="flex flex-col gap-2">
        <div class="flex gap-2 justify-between">
          <FlightProcessingBadge
            v-if="processing"
            :bucket="processing"
          />
          <RiskBadge
            v-else
            :risk="flight.risk?.riskLevel"
          />
          <div class="flex gap-2 items-center justify-end">
            <FlightStatusBadge :status="flight.status" />
            <UTooltip
              v-if="isShortStripLanding"
              :text="shortStripLandingTooltip"
              :delay-duration="0"
            > 
              <div
                class="flex items-center justify-center shrink-0 rounded-full bg-amber-500 text-amber-900 size-6"
              >
                <RulerIcon
                  class="size-4"
                />
              </div>
            </UTooltip>
            <UTooltip
              v-if="isLateNightFlight"
              text="Late night flight. Departure or arrival between 00:00 and 05:00"
              :delay-duration="0"
            >
              <UIcon
                name="ic-round-nights-stay"
                class="size-5 text-info-900"
              />
            </UTooltip>
            <FlightEventsIcon :events="flight.events" />
          </div>
        </div>
        <div>
          <div class="text-brand-500 text-lg">
            {{ duration }}
          </div>
          <div class="text-gray-500 text-sm">
            {{ dateLabel }} |
            <RouterLink
              :to="`/airports/${airports.origin}`"
              class="hover:underline"
            >
              {{ airports.origin }}
            </RouterLink>
            to
            <RouterLink
              :to="`/airports/${airports.destination}`"
              class="hover:underline"
            >
              {{ airports.destination }}
            </RouterLink>
          </div>
        </div>
      </div>
    </template>

    <div>
      <h3 class="text-gray-500 text-sm font-cta mb-2">
        Risk Breakdown
      </h3>
      <RiskBreakdown
        :flight="flight"
        type="hybrid"
      />
    </div>
    <template #footer>
      <div class="flex items-center justify-end">
        <UButton
          label="View Flight"
          size="sm"
          :to="`/flights/${flight.id}`"
        />
      </div>
    </template>
  </UCard>
</template>
  